import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Component, computed, Injector, inject, resource, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import {
  BbitGeoObject,
  BbitGeoObjectType,
  BbitI18n,
  BbitObjectArray,
  IBbitGeoCoordinates,
  IBbitMapOptions,
} from '@bbitgmbh/bbit.utils';
import { BbitWebsiteService, I18nPipe } from '@bbitgmbh/website.sdk';
import { DateTime } from 'luxon';
import { BehaviorSubject } from 'rxjs';
import { LoadingComponent } from '../../helper/loading/loading.component';
import { MapComponent } from '../../map/map.component';
import { IBbitCampsite, IBbitCampsiteBookingPeriodWithCampsites } from '../camping.interfaces';
import { CampingBookingOverlayComponent } from '../camping-booking-overlay/camping-booking-overlay.component';
import { CampingBookingOverlayRef } from '../camping-booking-overlay/camping-booking-overlay.ref';

@Component({
  selector: 'app-camping-map',
  imports: [MapComponent, LoadingComponent, I18nPipe],
  templateUrl: './camping-map.component.html',
  styleUrl: './camping-map.component.scss',
})
export class CampingMapComponent {
  private _overlay = inject(Overlay);
  private _breakpointObserver = inject(BreakpointObserver);
  private _website = inject(BbitWebsiteService);
  private _mapCenter = new BehaviorSubject<IBbitGeoCoordinates>({
    lat: 46.865769104890596,
    lng: 7.548949257805812,
  });
  private _mapZoom = new BehaviorSubject<number>(17);
  // biome-ignore lint/suspicious/noExplicitAny: required
  private _mapObjects = new BehaviorSubject<BbitGeoObject<{ campsite: any }>[]>([]);
  public mapOptions: IBbitMapOptions = {
    zoom: this._mapZoom,
    center: this._mapCenter,
    objects: this._mapObjects,
  };
  public id = signal('');
  public availableCount = computed(() => {
    let sum = 0;
    for (const o of this.period.value()?.campsites || []) {
      sum += o.capacity - o.availableCapacity;
    }
    return sum;
  });

  public reservedCount = computed(() => {
    let sum = 0;
    for (const o of this.period.value()?.campsites || []) {
      sum += o.availableCapacity;
    }
    return sum;
  });

  public period = resource<IBbitCampsiteBookingPeriodWithCampsites, { id: string }>({
    params: () => ({ id: this.id() }),
    loader: async ({ params }) => {
      const response = await fetch(
        `${this._website.getBlApiUrl()}/campsite/bookingPeriod/${encodeURIComponent(params.id)}`,
        {
          method: 'GET',
          headers: {
            Authorization: `${this._website.getAccessToken()}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error('call failed');
      }

      const result = await response.json();

      const objects: BbitGeoObject[] = [];
      for (const o of result.campsites) {
        // biome-ignore lint/suspicious/noExplicitAny: required
        let coordinates: any = [];
        switch (o?.coordinates.type) {
          case BbitGeoObjectType.MARKER:
            coordinates = BbitObjectArray.toNormalArray(o?.coordinates.coordinates)[0];
            break;
          case BbitGeoObjectType.POLYGON:
            coordinates = BbitObjectArray.toNormalArray(o?.coordinates.coordinates);
            break;
          default:
            continue;
        }
        const obj: BbitGeoObject = {
          _id: o._id,
          type: o.coordinates.type,
          coordinates,
          label: BbitI18n.t(o.designation),
          color: o.isAvailable ? 'blue' : 'red',
          onClick: () => {
            if (o.isAvailable) {
              this.openOverlay(o);
            }
          },
          data: { booking: o },
        };

        objects.push(obj);
      }
      this._mapObjects.next(objects.sort((a, b) => a.label?.localeCompare(b?.label || '') || 0));

      return result;
    },
  });

  constructor(private _activatedRoute: ActivatedRoute) {
    this._activatedRoute.params.pipe(takeUntilDestroyed()).subscribe(async (params) => {
      this.id.set(params['id']);
    });
  }

  formatDate(date: string): string {
    return DateTime.fromISO(date).toFormat('dd.LL.yyyy HH:mm');
  }

  public async openOverlay(campsite: IBbitCampsite): Promise<void> {
    const overlayConfig = new OverlayConfig({
      hasBackdrop: true,
      backdropClass: 'overlay-backdrop',
      panelClass: '',
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay.position().global().centerHorizontally().centerVertically(),
    });
    const overlayRef = this._overlay.create(overlayConfig);
    const overlayRefInstance = new CampingBookingOverlayRef(overlayRef);

    overlayRefInstance.observeClose().subscribe((reload) => {
      if (reload) {
        this.period.reload();
      }
      breakpoint.unsubscribe();
    });

    overlayRefInstance.setData({ campsite, period: this.period.value() });

    const injector = Injector.create({
      providers: [
        { provide: CampingBookingOverlayRef, useValue: overlayRefInstance },
        { provide: BbitWebsiteService, useValue: this._website },
      ],
    });
    const containerPortal = new ComponentPortal(CampingBookingOverlayComponent, null, injector);
    overlayRef.attach(containerPortal);
    const breakpoint = this._breakpointObserver.observe(Breakpoints.XSmall).subscribe((size) => {
      if (size.matches) {
        overlayRef.updateSize({ width: '100%', height: '100%' });
      } else {
        overlayRef.updateSize({
          minWidth: '400px',
          maxWidth: '600px',
          width: '80%',
          height: '80%',
          maxHeight: '80%',
        });
      }
    });
    overlayRef.backdropClick().subscribe((): void => {
      breakpoint.unsubscribe();
      overlayRefInstance.close();
    });
  }
}
